import { isDevelopmentEnvironment, AppSettingsHelpers } from '@invision/core';
import type { EnvironmentValue } from '../constants';
import { ENVIRONMENT_OPTIONS } from '../constants';

/**
 * Environment Detection Helper
 *
 * Gets current environment details including:
 * - Whether running in development mode
 * - Detected environment from hostname (production only)
 * - Whether there's an environment detection error
 *
 * @returns Object with environment details and error state
 */
export function getEnvironmentDetails(): {
  isDevelopment: boolean;
  detectedEnvironment: EnvironmentValue | null;
  hasEnvironmentError: boolean;
} {
  const isDevelopment = isDevelopmentEnvironment();

  // Get raw environment from host and validate it
  let detectedEnvironment: EnvironmentValue | null = null;

  if (!isDevelopment) {
    const rawEnvironment = AppSettingsHelpers.getEnvironmentFromCurrentHost();
    detectedEnvironment = isValidEnvironmentValue(rawEnvironment)
      ? rawEnvironment
      : null;
  }

  const hasEnvironmentError = !isDevelopment && !detectedEnvironment;

  return {
    isDevelopment,
    detectedEnvironment,
    hasEnvironmentError
  };
}

/**
 * Type guard to check if a string value is a valid EnvironmentValue
 *
 * @param value - The string value to validate
 * @returns True if the value is a valid environment option
 */
export function isValidEnvironmentValue(
  value: string | null
): value is EnvironmentValue {
  if (!value) return false;
  const validValues = ENVIRONMENT_OPTIONS.map((option) => option.value);
  return validValues.includes(value as EnvironmentValue);
}
